/**
 * Cloudflare Turnstile (optionnel) — vérification humaine en ligne.
 * Activé seulement si `VITE_TURNSTILE_SITE_KEY` est défini et le navigateur est en ligne.
 */
const SCRIPT_ID = 'cf-turnstile-script';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement | string,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string;
    };
  }
}

export function turnstileSiteKey(): string | undefined {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  return key?.trim() || undefined;
}

export function shouldUseTurnstile(): boolean {
  return Boolean(turnstileSiteKey()) && typeof navigator !== 'undefined' && navigator.onLine;
}

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (document.getElementById(SCRIPT_ID)) {
    return new Promise((resolve) => {
      const check = (): void => {
        if (window.turnstile) resolve();
        else setTimeout(check, 50);
      };
      check();
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile_load_failed'));
    document.head.appendChild(s);
  });
}

export async function mountTurnstile(
  container: HTMLElement,
  onToken: (token: string | null) => void,
): Promise<{ reset: () => void } | null> {
  const sitekey = turnstileSiteKey();
  if (!sitekey || !navigator.onLine) {
    onToken(null);
    return null;
  }
  container.replaceChildren();
  const slot = document.createElement('div');
  container.appendChild(slot);
  try {
    await loadScript();
    if (!window.turnstile) return null;
    let widgetId = '';
    widgetId = window.turnstile.render(slot, {
      sitekey,
      theme: 'light',
      callback: (token) => onToken(token),
      'expired-callback': () => onToken(null),
    });
    return {
      reset: () => {
        onToken(null);
        window.turnstile?.reset(widgetId);
      },
    };
  } catch {
    onToken(null);
    return null;
  }
}

/** Exige un token si Turnstile est configuré ; sinon laisse passer (local / sans clé). */
export function assertTurnstileOk(token: string | null | undefined): boolean {
  if (!shouldUseTurnstile()) return true;
  return Boolean(token && token.length > 10);
}
